import { Link } from "react-router-dom";
import SEO, { breadcrumbSchema } from "@/components/SEO";
import { Shield, Zap, Heart, Gift, Award, Users, Building2, Sparkles, Mail, Phone, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

function Hero({ eyebrow, title, subtitle }) {
    return (
        <section className="rk-gradient-midnight rk-noise text-white">
            <div className="max-w-[1100px] mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
                <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold mb-4">{eyebrow}</div>
                <h1 className="font-serif text-5xl md:text-7xl font-light leading-[1.05]">{title}</h1>
                {subtitle && <p className="mt-5 text-white/75 text-lg max-w-2xl">{subtitle}</p>}
            </div>
            <div className="rk-divider-gold" />
        </section>
    );
}

function Section({ children, className = "" }) {
    return <section className={`max-w-[1100px] mx-auto px-4 md:px-8 lg:px-12 py-16 ${className}`}>{children}</section>;
}

export function About() {
    return (
        <div className="bg-rk-bg">
            <SEO
                title="About LoyltyKart — India's Luxury Rewards Marketplace"
                description="LoyltyKart is India's luxury digital gift card marketplace. We partner with 270+ brands — Tanishq, MakeMyTrip, Nykaa, Taj & more — to deliver instant rewards via WhatsApp in 10 seconds. Founded in 2026."
                path="/about"
                structuredData={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])}
            />
            <Hero eyebrow="Our Story" title="Gifts that actually arrive on time." subtitle="LoyltyKart is a luxury rewards marketplace built on a simple belief: the best gift is the one that lands the moment you mean it." />
            <Section>
                <div className="grid md:grid-cols-2 gap-12 items-start">
                    <div>
                        <h2 className="font-serif text-3xl md:text-4xl text-rk-navy font-light mb-5">Why we exist</h2>
                        <p className="text-rk-navy/80 leading-relaxed text-[17px]">
                            In a country where family is spread from Mumbai to Melbourne, the traditional gift economy has broken down. Sweets go stale. Jewellery takes weeks to pick. Parcels get lost. And the moment you wanted to create gets buried under logistics.
                        </p>
                        <p className="text-rk-navy/80 leading-relaxed text-[17px] mt-4">
                            LoyltyKart fixes this by delivering a premium digital gift card from 270+ Indian brands in **10 seconds via WhatsApp**. Tanishq for your mother. MakeMyTrip for your partner's overdue holiday. Taj for a friend who just got promoted. One tap. Delivered.
                        </p>
                    </div>
                    <div>
                        <h2 className="font-serif text-3xl md:text-4xl text-rk-navy font-light mb-5">What we stand for</h2>
                        <ul className="space-y-4">
                            {[
                                { I: Zap, t: "Instant delivery", d: "A gift that takes 3 days is already a failure." },
                                { I: Shield, t: "100% genuine", d: "Every brand we list is onboarded directly or via licensed aggregators." },
                                { I: Heart, t: "Thoughtful, not transactional", d: "Our product nudges you to write the message, schedule the moment, pick the right brand." },
                                { I: Award, t: "Luxury over low-price", d: "We're not the cheapest marketplace — we're the most trusted." },
                            ].map((v, i) => (
                                <li key={i} className="flex gap-4">
                                    <div className="w-10 h-10 border border-rk-gold/40 flex items-center justify-center shrink-0 text-rk-gold-deep"><v.I className="w-4 h-4" /></div>
                                    <div>
                                        <div className="font-semibold text-rk-navy">{v.t}</div>
                                        <div className="text-sm text-rk-muted mt-0.5">{v.d}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Section>
            <Section className="bg-rk-navy text-white !max-w-full">
                <div className="max-w-[1100px] mx-auto">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold mb-3">By the numbers</div>
                    <h2 className="font-serif text-4xl md:text-5xl font-light mb-10">A fast-growing marketplace.</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-l border-white/15">
                        {[
                            { n: "270+", l: "Brands" },
                            { n: "18", l: "Categories" },
                            { n: "10s", l: "Delivery" },
                            { n: "100%", l: "Genuine" },
                        ].map((s, i) => (
                            <div key={i} className="border-r border-b border-white/15 p-8 text-center">
                                <div className="font-serif text-5xl text-rk-gold">{s.n}</div>
                                <div className="mt-2 text-[10px] uppercase tracking-[0.26em] text-white/70">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>
        </div>
    );
}

export function HowItWorks() {
    return (
        <div className="bg-rk-bg">
            <SEO
                title="How LoyltyKart Works — Send a Gift Card in 10 Seconds"
                description="Sending a digital gift card on LoyltyKart is 3 simple steps: pick a brand, choose denomination + recipient, pay by UPI/card. Delivered via WhatsApp instantly."
                path="/how-it-works"
                structuredData={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "How It Works", path: "/how-it-works" }])}
            />
            <Hero eyebrow="The Process" title="From thought to their hand in 10 seconds." subtitle="No printing. No shipping. No postage. Just three taps and the right gift reaches the right person." />
            <Section>
                <div className="space-y-12">
                    {[
                        { n: "01", t: "Pick the brand", d: "Search or browse 270+ brands across jewellery, travel, fashion, F&B, hotels, electronics, beauty & more. See real discounts (3–12% off) on every card.", icon: Sparkles },
                        { n: "02", t: "Choose for yourself or gift", d: "Select a denomination (₹250 to ₹1 lakh+), pick 'For myself' or 'Gift to someone', add the recipient's WhatsApp number and a personal message. Schedule delivery if you want.", icon: Gift },
                        { n: "03", t: "Pay securely, delivered instantly", d: "Pay via UPI, card, or netbanking (secured by Razorpay). Voucher lands on the recipient's WhatsApp within 10 seconds — with your message, beautifully formatted.", icon: Zap },
                    ].map((s) => (
                        <div key={s.n} className="grid md:grid-cols-12 gap-8 items-start border-t border-rk-border pt-12">
                            <div className="md:col-span-2 font-serif text-7xl text-rk-gold-deep/30 font-light leading-none">{s.n}</div>
                            <div className="md:col-span-7">
                                <h3 className="font-serif text-3xl text-rk-navy font-light mb-3">{s.t}</h3>
                                <p className="text-rk-navy/75 leading-relaxed text-[17px]">{s.d}</p>
                            </div>
                            <div className="md:col-span-3 flex md:justify-end">
                                <div className="w-16 h-16 border border-rk-gold flex items-center justify-center text-rk-gold-deep">
                                    <s.icon className="w-6 h-6" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-16 text-center">
                    <Link to="/catalog" className="rk-btn-primary inline-block">Start Gifting</Link>
                </div>
            </Section>
        </div>
    );
}

const FAQS = [
    { q: "How fast do gift cards actually arrive?", a: "Within 10 seconds of successful payment. We send via WhatsApp as the primary channel, with email and in-app as backup." },
    { q: "Are the gift cards genuine?", a: "100%. We source directly from brand partners or via licensed aggregators (Pine Labs / Woohoo). Every voucher carries a real, active balance you can redeem." },
    { q: "What if the recipient doesn't receive the WhatsApp message?", a: "The voucher is always available under 'My Vouchers' in your LoyltyKart account. You can also forward the code manually." },
    { q: "Can I schedule a gift for a future date?", a: "Yes. At checkout you can pick a specific date and time — we'll deliver exactly then." },
    { q: "Can I get a refund?", a: "Unredeemed vouchers can be cancelled within 24 hours of purchase. Once the recipient has used any part of the balance, refunds are not possible (brand policy)." },
    { q: "How do corporate bulk orders work?", a: "We're onboarding our corporate gifting module now. For urgent bulk orders (50+ vouchers) email corporate@loyltykart.in." },
    { q: "Do gift cards expire?", a: "Most cards are valid for 1 year from issuance, though some brand cards have longer validity. Exact validity is shown on each product page." },
    { q: "What payment methods are accepted?", a: "UPI, all major credit & debit cards (Visa, Mastercard, Amex, Rupay), netbanking from 50+ banks, and wallets — all via Razorpay secure checkout." },
    { q: "Why should I buy from LoyltyKart vs. the brand directly?", a: "Every card on LoyltyKart ships with an instant discount (3–12% off face value). A ₹10,000 Tanishq card costs you ~₹9,700. Plus: one app for 270+ brands, WhatsApp delivery, and a real gifting experience." },
    { q: "Is my data and payment information safe?", a: "Yes. We don't store your card details. All payments go through Razorpay (PCI-DSS Level 1 compliant). Your phone number is only used for login and order updates." },
];

export function FAQs() {
    const [open, setOpen] = useState(0);
    return (
        <div className="bg-rk-bg">
            <SEO
                title="Frequently Asked Questions — LoyltyKart"
                description="Answers to common questions about LoyltyKart: delivery speed, refunds, authenticity, corporate gifting, payment methods, and more."
                path="/faqs"
                structuredData={[
                    { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQS.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
                    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "FAQs", path: "/faqs" }]),
                ]}
            />
            <Hero eyebrow="Answers" title="Frequently asked questions." />
            <Section>
                <div className="max-w-3xl mx-auto space-y-3">
                    {FAQS.map((f, i) => (
                        <div key={i} className="bg-white border border-rk-border">
                            <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between px-6 py-5 text-left">
                                <span className="font-serif text-lg text-rk-navy">{f.q}</span>
                                {open === i ? <ChevronUp className="w-5 h-5 text-rk-gold" /> : <ChevronDown className="w-5 h-5 text-rk-muted" />}
                            </button>
                            {open === i && <div className="px-6 pb-5 text-rk-navy/75 leading-relaxed text-[15px]">{f.a}</div>}
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
}

export function Contact() {
    return (
        <div className="bg-rk-bg">
            <SEO
                title="Contact LoyltyKart"
                description="Get in touch with LoyltyKart — customer support, corporate gifting, press, and partnerships."
                path="/contact"
                structuredData={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])}
            />
            <Hero eyebrow="Connect" title="Talk to us." />
            <Section>
                <div className="grid md:grid-cols-3 gap-0 border-t border-l border-rk-border">
                    {[
                        { I: MessageSquare, t: "Customer Support", d: "hello@loyltykart.in", sub: "Response within 4 hours" },
                        { I: Building2, t: "Corporate Gifting", d: "corporate@loyltykart.in", sub: "For bulk orders 50+" },
                        { I: Users, t: "Partnerships", d: "partners@loyltykart.in", sub: "Brand & distribution" },
                    ].map((c, i) => (
                        <div key={i} className="border-r border-b border-rk-border p-8 bg-white">
                            <div className="w-12 h-12 border border-rk-gold flex items-center justify-center text-rk-gold-deep mb-5"><c.I className="w-4 h-4" /></div>
                            <h3 className="font-serif text-2xl text-rk-navy mb-2">{c.t}</h3>
                            <a href={`mailto:${c.d}`} className="rk-link-gold text-rk-gold-deep font-semibold">{c.d}</a>
                            <div className="text-xs text-rk-muted mt-2 uppercase tracking-wider">{c.sub}</div>
                        </div>
                    ))}
                </div>
                <div className="mt-12 flex items-center gap-8 text-rk-navy">
                    <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-rk-gold-deep" /> <span className="font-semibold">1800-123-4567</span></div>
                    <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-rk-gold-deep" /> hello@loyltykart.in</div>
                </div>
            </Section>
        </div>
    );
}

export function Terms() {
    return (
        <div className="bg-rk-bg">
            <SEO title="Terms of Service" description="LoyltyKart terms of service and user agreement." path="/terms" noindex />
            <Hero eyebrow="Legal" title="Terms of service." />
            <Section>
                <div className="max-w-3xl mx-auto prose prose-lg text-rk-navy/80 leading-relaxed space-y-5 text-[15px]">
                    <p>Effective date: January 2026. By using LoyltyKart you agree to these terms.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">Who we are</h3>
                    <p>LoyltyKart is operated by TagNPay Technologies Pvt Ltd ("LoyltyKart", "we", "us"). We operate a digital marketplace for gift cards, vouchers and experiences from third-party brands.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">Account & age</h3>
                    <p>You must be 18 or older to purchase. You're responsible for keeping your mobile number and login secure. Impersonation or fraudulent purchases will result in account termination and possible legal action.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">Vouchers</h3>
                    <p>All vouchers are issued by the respective brand partners. Their terms and validity apply. LoyltyKart is a distributor and not the issuer. Complaints about redemption must ultimately be resolved by the brand; we will assist in good faith.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">Payment & pricing</h3>
                    <p>Prices are in INR and inclusive of applicable taxes. Payments are processed via Razorpay. Discounts shown are off the face value of the voucher.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">Refunds</h3>
                    <p>Unredeemed vouchers can be cancelled within 24 hours of purchase by writing to hello@loyltykart.in. Once the brand has marked the voucher as issued or the recipient has used any part, refunds are not possible.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">Liability</h3>
                    <p>Our liability in connection with any order is limited to the amount paid for that order. We are not liable for brand-side issues, expired vouchers not redeemed in time, or consequential damages.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">Governing law</h3>
                    <p>These terms are governed by the laws of India. Any dispute is subject to the exclusive jurisdiction of the courts of Bangalore.</p>
                </div>
            </Section>
        </div>
    );
}

export function Privacy() {
    return (
        <div className="bg-rk-bg">
            <SEO title="Privacy Policy" description="LoyltyKart privacy policy — how we collect, use and protect your data." path="/privacy" noindex />
            <Hero eyebrow="Legal" title="Privacy policy." />
            <Section>
                <div className="max-w-3xl mx-auto prose prose-lg text-rk-navy/80 leading-relaxed space-y-5 text-[15px]">
                    <p>We respect your privacy. This policy explains what we collect and what we do with it.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">What we collect</h3>
                    <p>Your mobile number (for login & order updates), name and email (if provided), order history, and IP address. We do not store your card/UPI details — those go directly to Razorpay.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">How we use it</h3>
                    <p>To fulfil your orders, deliver vouchers via WhatsApp/SMS/email, provide customer support, and (with consent) send occasional product updates.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">Who we share with</h3>
                    <p>Brand partners & voucher aggregators (to fulfil orders), Razorpay (payments), analytics providers (anonymised usage), and legal authorities where required.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">Your rights</h3>
                    <p>You can request deletion of your account and data at any time by writing to privacy@loyltykart.in. We keep transactional records for 7 years as required by Indian tax law.</p>
                </div>
            </Section>
        </div>
    );
}

export function RefundPolicy() {
    return (
        <div className="bg-rk-bg">
            <SEO title="Refund & Cancellation Policy" description="LoyltyKart refund and cancellation policy for digital gift cards." path="/refund-policy" noindex />
            <Hero eyebrow="Legal" title="Refunds & cancellations." />
            <Section>
                <div className="max-w-3xl mx-auto text-rk-navy/80 leading-relaxed space-y-5 text-[15px]">
                    <p>Because digital gift cards are fulfilled instantly, refunds are governed by the brand's own policies.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">Within 24 hours, unredeemed</h3>
                    <p>Email hello@loyltykart.in. If the brand confirms the voucher has not been redeemed or marked as issued on their side, we'll refund your payment to the original method within 5–7 business days.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">After 24 hours or if partially redeemed</h3>
                    <p>Refunds are not possible once the voucher is issued and active in the recipient's account, or once they have used part of the balance. The remaining balance stays on the voucher until expiry.</p>
                    <h3 className="font-serif text-2xl text-rk-navy mt-8 mb-3">Failed deliveries</h3>
                    <p>If a voucher was not delivered due to a technical issue on our side, we'll either redeliver or refund, at your choice.</p>
                </div>
            </Section>
        </div>
    );
}
